import {
    Button,
    ContextMenu,
    FlexColumn,
    Grid,
    IGuiBinder,
    Index,
    isNullOrEmpty,
} from '@synqo/frontend-component-library';
import { IconPlus } from '@tabler/icons-react';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { RemarkInput } from './RemarkInput';

interface IRemarksOptions<T> {
    data: T;
    remarkTypes: [Index<T>, string][];
    canEdit: boolean;
    binder: IGuiBinder<T>;
    menuClassName?: string;
}

interface IRemarks {
    hasRemarks: boolean;
    addRemarkMenu: ReactNode;
    remarkInputs: ReactNode;
}

// @ai-generated 2026-10-27T16:15:27+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function useRemarks<T>({ remarkTypes, data, canEdit, binder, menuClassName }: IRemarksOptions<T>): IRemarks {
    const [remarksShown, setRemarksShown] = useState(
        remarkTypes.filter((remark) => !isNullOrEmpty(data[remark[0]] as string | undefined)),
    );

    const remarksNotShown = useMemo(
        () => remarkTypes.filter((remark) => !remarksShown.map((shown) => shown[0]).includes(remark[0])),
        [remarksShown, remarkTypes],
    );

    const addRemark = useCallback(
        (remark: [Index<T>, string]) => {
            setRemarksShown((latest) => {
                const remarksNames = latest.map((shown) => shown[0]);
                if (remarksNames.includes(remark[0])) {
                    return latest;
                }
                return [...latest, remark];
            });
        },
        [setRemarksShown],
    );

    const addRemarkButtons = useMemo(
        () =>
            remarksNotShown.map((remark) => (
                <Button
                    key={`btn_select_remark_${remark[0]}`}
                    appearance="transparent"
                    onClick={() => addRemark(remark)}
                    fullWidth
                    inheritColor
                >
                    {remark[1]}
                </Button>
            )),
        [remarksNotShown, addRemark],
    );

    const addRemarkMenu = canEdit && (
        <ContextMenu
            name={'add_remarks'}
            icon={IconPlus}
            variant="outline"
            buttonSize="xs"
            padding="xxs"
            locked={addRemarkButtons.length === 0}
            className={menuClassName}
        >
            <FlexColumn align="start" justify="start" rowGap="none" gap="none">
                {addRemarkButtons}
            </FlexColumn>
        </ContextMenu>
    );

    const remarkInputs = (
        <Grid>
            {remarksShown.map((remark) => (
                <RemarkInput<T>
                    key={`remark_input_${remark[0]}`}
                    binder={binder}
                    propertyName={remark[0]}
                    value={remark[1]}
                    setRemarksShown={setRemarksShown}
                    canEdit={canEdit}
                />
            ))}
        </Grid>
    );

    return { hasRemarks: remarksShown.length > 0, addRemarkMenu, remarkInputs };
}
